import React, { useState, useEffect, useMemo, useRef } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useStyles } from './styles';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { can } from '../../auth/permissions';
import { usePermissions } from '../../auth/useCan';
import { HIDDEN_ROUTE_SECTIONS, NAV_SECTIONS, NavItem, NavSection, NavSectionId } from './navConfig';
import { normalizePath, pickActiveItem, sectionForPath } from './navActive';

interface ILeftNavProps {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const LeftNavigationBar = ({ isMobileOpen = false, onMobileClose }: ILeftNavProps) => {
  const location = useLocation();
  const classes = useStyles();
  const permissions = usePermissions();

  // Only the items the user may open; a section disappears together with its last item.
  const sections = useMemo<NavSection[]>(
    () => NAV_SECTIONS
      .map((section) => ({ ...section, items: section.items.filter((item) => can(permissions, item.requires)) }))
      .filter((section) => section.items.length > 0),
    [permissions]
  );

  const path = normalizePath(location.pathname);
  const archivedView = new URLSearchParams(location.search).get('archived') === 'true';
  const active = useMemo(() => pickActiveItem(sections, path), [sections, path]);
  const sectionFromLocation = sectionForPath(sections, path, active, HIDDEN_ROUTE_SECTIONS);

  const [expandedSection, setExpandedSection] = useState<NavSectionId | null>(sectionFromLocation);
  const [archivedOpen, setArchivedOpen] = useState<boolean>(archivedView || !!active?.item.archived);

  useEffect(() => {
    if (sectionFromLocation != null) {
      setExpandedSection(sectionFromLocation);
    }
  }, [sectionFromLocation]);

  useEffect(() => {
    if (active?.item.archived) setArchivedOpen(true);
  }, [active]);

  // Close the mobile menu on route change. The props are read through refs because PageContainer
  // passes a new `onMobileClose` arrow on every render, which must not re-run this effect.
  const mobileRef = useRef({ isMobileOpen, onMobileClose });
  mobileRef.current = { isMobileOpen, onMobileClose };
  useEffect(() => {
    const { isMobileOpen: open, onMobileClose: close } = mobileRef.current;
    if (open && close) close();
  }, [location.pathname]);

  const handleAccordionChange = (section: NavSectionId) => (_e: React.SyntheticEvent, expanded: boolean) => {
    setExpandedSection(expanded ? section : null);
  };

  const renderNavLink = (item: NavItem) => {
    const isActive = active?.item === item;
    return (
      <Link
        to={item.archived ? `/${item.link}?archived=true` : `/${item.link}`}
        key={item.link}
        aria-current={isActive ? 'page' : undefined}
        className={classNames(classes.itemContainer, { [classes.activeRoute]: isActive })}
      >
        <div className={classes.linkContent}>
          <div className={classes.itemIcon}>
            <item.Icon fontSize="small" aria-hidden="true" />
          </div>
          <div className={classes.itemTitle}>{item.title}</div>
        </div>
      </Link>
    );
  };

  const renderSection = (section: NavSection) => {
    const current = section.items.filter((item) => !item.archived);
    const archived = section.items.filter((item) => item.archived);
    return (
      <Accordion
        key={section.id}
        className={classes.customAcordion}
        expanded={expandedSection === section.id}
        onChange={handleAccordionChange(section.id)}
      >
        <AccordionSummary id={`${section.id}-panel-header`} aria-controls={`${section.id}-panel-content`} expandIcon={<ExpandMoreIcon />}>
          <span className={classes.sectionHeader}>{section.title}</span>
        </AccordionSummary>
        <AccordionDetails id={`${section.id}-panel-content`}>
          <div className={classes.itemsWrapper}>{current.map(renderNavLink)}</div>
          {archived.length > 0 && (
            <Accordion
              className={classNames(classes.customAcordion, classes.nestedAccordion)}
              expanded={archivedOpen}
              onChange={(_e, expanded) => setArchivedOpen(expanded)}
            >
              <AccordionSummary
                id={`${section.id}-archived-header`}
                aria-controls={`${section.id}-archived-content`}
                expandIcon={<ExpandMoreIcon />}
              >
                <span className={classes.sectionHeaderArchived}>Archived companies</span>
              </AccordionSummary>
              <AccordionDetails id={`${section.id}-archived-content`}>
                <div className={classes.itemsWrapper}>{archived.map(renderNavLink)}</div>
              </AccordionDetails>
            </Accordion>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <nav className={classNames(classes.root, { [classes.mobileOpen]: isMobileOpen })} aria-label="Main navigation">
      <div className={classes.mobileHeader}>
        <span className={classes.mobileTitle}>Menu</span>
        <button className={classes.mobileCloseBtn} onClick={onMobileClose} aria-label="Close navigation">
          <CloseIcon fontSize="small" />
        </button>
      </div>
      <div className={classes.navContent}>
        {sections.map(renderSection)}
      </div>
    </nav>
  );
};

export default LeftNavigationBar;
